import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toSentenceCase } from '@utils/textCase.util'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Breadcrumbs, Button } from '@enamelai/design-system'
import { Icon as ArrowLeftIcon } from '@enamelai/design-system/icons/Arrow_2_left'
import { InputField } from '@components/common'
import ReportsSidebar from '@components/pages/Reports/ReportsSidebar.component'
import ErrorFallback from '@components/common/ErrorBoundary/ErrorFallback.component'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  FIRST_LOAD_OVERLAY_MS,
  FIRST_LOAD_OVERLAY_STYLE,
  waitAtLeast,
} from '@utils/waitAtLeast.util'
import {
  getEngagementSubmenuLabel,
  getFinanceSubmenuLabel,
  getOperationsSubmenuLabel,
  getPatientsSubmenuLabel,
  getProviderPerformanceSubmenuLabel,
  REPORTS_SIDEBAR_BUNDLE_ITEMS,
} from '@constants/Reports/reportsPage.constants'
import {
  getPresetsPathWithTab,
  type PresetsReturnState,
} from '@constants/Reports/presetsAutomation.constants'
import { useBlurFocusedInputOnNonFieldClick } from '@hooks/reports/useBlurFocusedInputOnNonFieldClick'
import './ReportsStyle.css'
import { useClinicProviders } from '@hooks/useClinicProviders'
import { ReportsRefreshProvider } from '@context/ReportsRefreshContext'

export type ReportsOutletContext = {
  searchQuery: string
  setSearchQuery: (q: string) => void
  isPageReady: boolean
}

const REPORTS_LOADING_CONTAINER = '.reports-page'

function isReportsHubPath(pathname: string): boolean {
  return (
    pathname === '/report' || /^\/report\/bundles\/[^/]+\/?$/.test(pathname)
  )
}

function isFirefoxBrowser(): boolean {
  if (typeof navigator === 'undefined') return false
  return /firefox\//i.test(navigator.userAgent)
}

function getReportPageTitle(pathname: string): string {
  const reportMatch = /^\/report\/bundles\/([^/]+)\/([^/]+)/.exec(pathname)
  if (reportMatch) {
    const [, bundleId, reportSegment] = reportMatch
    const bundleLabel =
      REPORTS_SIDEBAR_BUNDLE_ITEMS.find((b) => b.id === bundleId)?.label ?? null
    switch (bundleId) {
      case 'patients':
        return (
          getPatientsSubmenuLabel(reportSegment) ?? bundleLabel ?? 'Patients'
        )
      case 'engagement':
        return (
          getEngagementSubmenuLabel(reportSegment) ??
          bundleLabel ??
          'Engagement'
        )
      case 'operations':
        return (
          getOperationsSubmenuLabel(reportSegment) ??
          bundleLabel ??
          'Operations'
        )
      case 'finance':
        return getFinanceSubmenuLabel(reportSegment) ?? bundleLabel ?? 'Finance'
      case 'provider-performance':
        return (
          getProviderPerformanceSubmenuLabel(reportSegment) ??
          bundleLabel ??
          'Provider Performance'
        )
      default:
        return bundleLabel ?? 'Report Bundles'
    }
  }

  const bundleMatch = /^\/report\/bundles\/([^/]+)/.exec(pathname)
  if (bundleMatch) {
    const bundleId = bundleMatch[1]
    const item = REPORTS_SIDEBAR_BUNDLE_ITEMS.find((b) => b.id === bundleId)
    if (item) return item.label
  }

  if (/^\/report\/custom/.test(pathname)) return 'Custom Reports'
  if (/^\/report\/presets/.test(pathname)) return 'Presets and Automation'

  return 'Report Bundles'
}

function Reports(): React.ReactElement {
  useClinicProviders()
  const location = useLocation()
  const navigate = useNavigate()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { pathname } = location
  const [isPageReady, setIsPageReady] = useState(false)
  const isPageReadyRef = useRef(false)
  const previewMatch = /^\/report\/bundles\/([^/]+)\/([^/]+)\/preview\/?$/.exec(
    pathname,
  )
  const isReportPreview = previewMatch != null
  const isCustomBuilder = /^\/report\/custom(\/.*)?$/i.test(pathname)
  const isReportsHub = isReportsHubPath(pathname)
  const useOuterShell = isReportPreview || isCustomBuilder
  const showTopHeader = true
  const showBackButton = isReportPreview || isCustomBuilder

  const pageTitle = useMemo(() => {
    const raw = isReportPreview
      ? 'Report Preview'
      : isCustomBuilder
        ? 'Custom Report Builder'
        : getReportPageTitle(pathname)
    return toSentenceCase(raw)
  }, [isCustomBuilder, isReportPreview, pathname])

  const handleHeaderBack = (): void => {
    const presetsTab = (location.state as PresetsReturnState | null)
      ?.presetsReturnTab
    if (presetsTab) {
      navigate(getPresetsPathWithTab(presetsTab))
      return
    }
    if (previewMatch) {
      const [, categoryId, reportSlug] = previewMatch
      navigate(`/report/bundles/${categoryId}/${reportSlug}`)
      return
    }
    navigate('/report')
  }

  const [searchQuery, setSearchQuery] = useState('')
  const [refreshNonce, setRefreshNonce] = useState(0)
  const [reportError, setReportError] = useState<string | null>(null)

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(event.target.value)
  }

  const handleClearSearch = (): void => {
    setSearchQuery('')
  }

  useEffect(() => {
    setReportError(null)
  }, [pathname])

  const reportsShellRef = useRef<HTMLDivElement>(null)
  useBlurFocusedInputOnNonFieldClick(reportsShellRef)

  useLayoutEffect(() => {
    if (!isReportsHub) {
      hideLoading(REPORTS_LOADING_CONTAINER)
      hideLoading('report-preview-main')
      if (!isPageReadyRef.current) {
        isPageReadyRef.current = true
        setIsPageReady(true)
      }
      return
    }
    if (isPageReadyRef.current) return
    showLoading({
      container: REPORTS_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
  }, [isReportsHub, showLoading, hideLoading])

  useEffect(() => {
    if (!isReportsHub || isPageReadyRef.current) return
    let cancelled = false
    const startedAt = Date.now()
    showLoading({
      container: REPORTS_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
    void waitAtLeast(startedAt, FIRST_LOAD_OVERLAY_MS).then(() => {
      if (cancelled) return
      isPageReadyRef.current = true
      setIsPageReady(true)
      hideLoading(REPORTS_LOADING_CONTAINER)
    })
    return () => {
      cancelled = true
    }
  }, [isReportsHub, hideLoading, showLoading])

  useEffect(() => {
    return () => {
      hideLoading(REPORTS_LOADING_CONTAINER)
    }
  }, [hideLoading])

  useEffect(() => {
    const el = reportsShellRef.current
    if (!el || !isFirefoxBrowser()) return
    el.classList.add('reports-page--firefox-ux')
    return () => {
      el.classList.remove('reports-page--firefox-ux')
    }
  }, [])

  const outletContext: ReportsOutletContext = {
    searchQuery,
    setSearchQuery,
    isPageReady,
  }

  const showHubChrome = !isReportPreview && !isCustomBuilder

  const pageHeading = isReportPreview ? (
    <Breadcrumbs
      className="reports-page-top-header__breadcrumbs"
      items={[
        {
          id: 'report',
          label: toSentenceCase(getReportPageTitle(pathname)),
          onClick: handleHeaderBack,
        },
        { id: 'preview', label: 'Preview' },
      ]}
      showBackIcon
      onBackClick={handleHeaderBack}
    />
  ) : (
    <h2
      id="reports-page-top-heading"
      className="reports-page-top-header__title"
    >
      {showBackButton && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="reports-page-top-header__back"
          aria-label="Back"
          onClick={handleHeaderBack}
          leftIcon={<ArrowLeftIcon aria-hidden />}
        >
          <span className="visually-hidden">Back</span>
        </Button>
      )}
      <span>{pageTitle}</span>
    </h2>
  )

  return (
    <div
      ref={reportsShellRef}
      className={`reports-page${isPageReady ? ' is-ready' : ''}${
        isReportPreview ? ' reports-page--preview' : ''
      }${!showHubChrome ? ' reports-page--no-hub' : ''}`}
    >
      {showHubChrome && (
        <div className="reports-nav-bar">
          <InputField
            placeholder="Search"
            leftIcon={<span className="mgc_search_line" />}
            rightIcon={
              searchQuery.length > 0 ? (
                <span className="mgc_close_line" />
              ) : undefined
            }
            value={searchQuery}
            onChange={handleSearch}
            onRightIconClick={handleClearSearch}
            ariaLabel="Search reports"
          />
          <div className="reports-mc-header">
            {pageHeading}
            <div
              id="reports-page-top-header-actions"
              className="reports-page-top-header__actions"
            />
          </div>
        </div>
      )}
      <div className="reports-main-content-wrapper">
        {showHubChrome && (
          <div className="reports-page-rail">
            <ReportsSidebar searchQuery={searchQuery} />
          </div>
        )}
        <div className="reports-page-canvas">
          {showTopHeader && !showHubChrome && (
            <div className="reports-page-top-header">
              {pageHeading}
              <div
                id="reports-page-top-header-actions"
                className="reports-page-top-header__actions"
              />
            </div>
          )}
          <div className="reports-page-body">
            <div
              className={
                useOuterShell ? 'reports-preview-outer' : 'reports-main-shell'
              }
            >
              {reportError ? (
                <ErrorFallback
                  variant="page"
                  onReset={() => {
                    setReportError(null)
                    setRefreshNonce((value) => value + 1)
                  }}
                />
              ) : (
                <ReportsRefreshProvider
                  refreshKey={refreshNonce}
                  setReportError={setReportError}
                >
                  <Outlet context={outletContext} />
                </ReportsRefreshProvider>
              )}
            </div>
          </div>
        </div>
        {(isReportPreview || isCustomBuilder) && (
          <div
            id="reports-preview-pdf-rail"
            className="reports-preview-pdf-rail"
          />
        )}
      </div>
    </div>
  )
}

export default Reports
